import { useQuery } from '@tanstack/react-query';
import {
  CostCodeGroupByIdQuery,
  CostCodeGroupByIdQueryVariables,
  CostCodeGroupsQuery,
  CostCodeGroupsQueryVariables,
} from '__generated__/graphql';
import classNames from 'classnames';
import { GroupPicker } from 'components';
import { IGroupPickerProps } from 'components/domain/pickers/GroupPicker/GroupPicker';
import { useOpenable } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useScrollerRef from 'hooks/ui/useScrollerRef';
import _ from 'lodash';
import { ClassName } from 'types/ClassName';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { executeFunctionsCurried } from 'utils/functionUtils';
import { costCodeGroupQuery, costCodeGroupsQuery } from './cost-code-group-picker-queries';

export interface ICostCodeGroupPickerProps
  extends Omit<IGroupPickerProps, 'data' | 'isLoading' | 'currentGroup' | 'dataError' | 'loadedAll'> {
  className?: ClassName;
}

type CostCodeGroupPickerGroup = NonNullArrayField<CostCodeGroupsQuery['costCodeGroups']>;

export default function CostCodeGroupPicker({
  value,
  includeNoneOption,
  className,
  onOpen,
  onClose,
  ...pickerProps
}: ICostCodeGroupPickerProps) {
  const graphqlClient = useGraphQLClient();

  const toggle = useOpenable();
  const { scroller, setScrollerRef } = useScrollerRef();

  const sectionSize = 10;
  const baseKey = useReactQueryBaseKey();

  const { data: selectedGroup } = useQuery({
    queryKey: [...baseKey, 'cost-code-group-picker-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const result = await graphqlClient.request<CostCodeGroupByIdQuery, CostCodeGroupByIdQueryVariables>({
        document: costCodeGroupQuery,
        variables: { groupId: value },
      });

      return result.costCodeGroups?.[0]?.groupName ?? null;
    },
    enabled: !!value,
  });

  async function getCostCodeGroups(after: string | null, first: number): Promise<CostCodeGroupPickerGroup[]> {
    const result = await graphqlClient.request<CostCodeGroupsQuery, CostCodeGroupsQueryVariables>({
      document: costCodeGroupsQuery,
      variables: {
        after,
        first,
        filter: {
          deletedOn: { isNull: true },
        },
      },
    });

    return result.costCodeGroups ?? [];
  }

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(scroller, [...baseKey, 'cost-code-group-picker'], getCostCodeGroups, sectionSize, {
    enabled: toggle.isOpen,
  });

  const classes = classNames('cost-code-picker', className);

  return (
    <GroupPicker
      currentGroup={selectedGroup ?? null}
      includeNoneOption={includeNoneOption}
      className={classes}
      setScrollerRef={setScrollerRef}
      loadedAll={loadedAll}
      dataError={isDataError}
      data={data}
      isLoading={isLoading}
      value={value}
      onOpen={executeFunctionsCurried(toggle.open, onOpen)}
      onClose={executeFunctionsCurried(toggle.close, onClose)}
      {...pickerProps}
    />
  );
}
